import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  UploadFile as UploadFileIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useCaasClient } from '../../hooks/useCaasClient';
import { deleteClient } from '../../../../services/api/endpoints/caas';
import { useAuth } from '../../../../contexts/AuthContext';
import ClientFormModal from './ClientFormModal';
import TeamFormModal from '../teams/TeamFormModal';
import BulkImportModal from '../teams/BulkImportModal';

const CaasClientDetailPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { client, teams, spaces, isLoading, error, refresh } = useCaasClient(clientId);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateTeamModalOpen, setIsCreateTeamModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  
  // Teams pagination and search
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [teamPage, setTeamPage] = useState(0);
  const [teamRowsPerPage, setTeamRowsPerPage] = useState(25);

  const handleBack = () => {
    navigate('/caas/clients');
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    refresh();
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!user?.token || !client) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);
      await deleteClient(user.token, client.id);
      navigate('/caas/clients');
    } catch (err: any) {
      console.error('Error deleting client:', err);
      setDeleteError(err.message || 'Failed to delete client');
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setDeleteError(null);
  };

  const handleCreateTeam = () => {
    setIsCreateTeamModalOpen(true);
  };

  const handleTeamCreated = () => {
    setIsCreateTeamModalOpen(false);
    refresh();
  };

  const handleImportTeams = () => {
    setIsImportModalOpen(true);
  };

  const handleImportSuccess = () => {
    setIsImportModalOpen(false);
    refresh();
  };

  const handleTeamClick = (teamId: string) => {
    navigate(`/caas/clients/${clientId}/teams/${teamId}`);
  };

  // Filter and paginate teams
  const filteredTeams = teams.filter((team) => {
    const searchLower = teamSearchQuery.toLowerCase();
    return (
      team.name.toLowerCase().includes(searchLower) ||
      team.address.toLowerCase().includes(searchLower) ||
      (team.building?.name || '').toLowerCase().includes(searchLower)
    );
  });

  const paginatedTeams = filteredTeams.slice(
    teamPage * teamRowsPerPage,
    teamPage * teamRowsPerPage + teamRowsPerPage
  );

  const handleTeamSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTeamSearchQuery(event.target.value);
    setTeamPage(0); // Reset to first page on search
  };

  const handleTeamPageChange = (_: unknown, newPage: number) => {
    setTeamPage(newPage);
  };

  const handleTeamRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTeamRowsPerPage(parseInt(event.target.value, 10));
    setTeamPage(0);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !client) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Partners
        </Button>
        <Alert severity="error">{error || 'Client not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Partners
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            {client.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Client Info Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Partner Information
        </Typography>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Address
            </Typography>
            <Typography variant="body1">{client.address}</Typography>
          </Box>
          {client.hubspot_subscription_id && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                HubSpot Subscription ID
              </Typography>
              <Typography variant="body1">{client.hubspot_subscription_id}</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Teams Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">Teams ({teams.length})</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={handleImportTeams}
              size="small"
            >
              Import Teams
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateTeam}
              size="small"
            >
              Create Team
            </Button>
          </Box>
        </Box>

        {/* Search Bar */}
        {teams.length > 0 && (
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search teams by name, address, or building..."
            value={teamSearchQuery}
            onChange={handleTeamSearchChange}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
          />
        )}

        {teams.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No teams yet. Create your first team to get started.
          </Typography>
        ) : filteredTeams.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No teams match your search.
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Building</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTeams.map((team) => (
                    <TableRow
                      key={team.id}
                      hover
                      onClick={() => handleTeamClick(team.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{team.name}</TableCell>
                      <TableCell>{team.address}</TableCell>
                      <TableCell>
                        {team.building ? `${team.building.name}` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredTeams.length}
              page={teamPage}
              onPageChange={handleTeamPageChange}
              rowsPerPage={teamRowsPerPage}
              onRowsPerPageChange={handleTeamRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Spaces Section */}
      {spaces.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Associated Spaces ({spaces.length})
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {spaces.map((space) => (
                  <TableRow key={space.id}>
                    <TableCell>{space.name}</TableCell>
                    <TableCell>{space.type || '—'}</TableCell>
                    <TableCell>
                      {space.address
                        ? `${space.address.city}, ${space.address.state}`
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Edit Modal */}
      <ClientFormModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={handleEditSuccess}
        client={client}
      />

      {/* Create Team Modal */}
      <TeamFormModal
        open={isCreateTeamModalOpen}
        onClose={() => setIsCreateTeamModalOpen(false)}
        onSuccess={handleTeamCreated}
        clientId={clientId}
      />

      {/* Bulk Import Modal */}
      <BulkImportModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={handleImportSuccess}
        clientId={clientId || ''}
        clientName={client.name}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Client</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to delete "{client.name}"?
            {teams.length > 0 && (
              <Box component="span" sx={{ display: 'block', mt: 2, fontWeight: 'bold' }}>
                Warning: This client has {teams.length} team{teams.length !== 1 ? 's' : ''}.
                Deleting the client will also delete all associated teams.
              </Box>
            )}
            {spaces.length > 0 && (
              <Box component="span" sx={{ display: 'block', mt: 1 }}>
                Note: This client has {spaces.length} associated space{spaces.length !== 1 ? 's' : ''}.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaasClientDetailPage;
